/**
 * Hausa (Boko orthography, Standard/Kano variety).
 *
 * NEEDS NATIVE REVIEW before this ships to users. It is written to be correct
 * and idiomatic, but no machine translation is a substitute for a Hausa
 * speaker reading it aloud. `localeRegistry` marks this locale
 * `reviewed: false` and the settings screen labels it as a preview, so the
 * claim the product makes matches the work that has actually been done.
 *
 * Two things a reviewer should look at first:
 *  1. Second-person forms. Hausa marks gender on "you" (ka / ki). These lines
 *     use the masculine ka, which is the common written default, but the
 *     assistant asks about gender during onboarding, so the right long-term
 *     answer is to re-render these lines once gender is known. See
 *     `docs/LEARNINGS.md`.
 *  2. Hooked letters. ɓ ɗ ƙ and ƴ must survive round-tripping through the
 *     speech synthesiser and any font fallback.
 */

import type { Copy } from './en'

export const ha: Copy = {
  ui: {
    appName: 'Namu',
    tagline: 'Yi magana da shi cikin yarenka.',

    beginLabel: 'Danna don farawa',
    resumeLabel: 'Danna don magana',

    status: {
      idle: 'A shirye',
      permission: 'Ana jiran makurufo',
      connecting: 'Ana haɗawa',
      listening: 'Ina saurare',
      userSpeaking: 'Ina jinka',
      processing: 'Ina tunani',
      modelSpeaking: 'Ina magana',
      interrupted: 'An tsaya',
      reconnecting: 'Ana sāke haɗawa',
      ended: 'An gama',
      error: 'An sami matsala',
      muted: 'An kashe makurufo',
      held: 'An dakatar',
    },

    waiting: {
      connecting: 'Ana ci gaba da haɗawa.',
      thinking: 'Namu na ci gaba da tunani.',
      waking: 'Namu na tashi daga barci. Amsa ta farko bayan dogon shiru na iya ɗaukar minti ɗaya.',
    },

    control: {
      mute: 'Kashe makurufo',
      unmute: 'Kunna makurufo',
      hold: 'Dakatar da tattaunawa',
      resume: 'Ci gaba da tattaunawa',
      end: 'Gama tattaunawa',
      start: 'Fara tattaunawa',
      captionsOn: 'Nuna rubutu',
      captionsOff: 'Ɓoye rubutu',
      repeat: 'Sāke faɗi',
      skip: 'Tsallake wannan tambaya',
      back: 'Baya',
      close: 'Rufe',
      retry: 'Sāke gwadawa',
      settings: 'Saitunan',
      profile: 'Abin da Namu ya sani',
      turnOnMic: 'Kunna makurufo',
      notNow: 'Ba yanzu ba',
    },

    ledger: {
      title: 'Abin da Namu ya ji',
      empty: 'Babu kome tukuna. Namu zai cika wannan yayin da kake magana.',
      editHint: 'Danna kowace amsa don canza ta',
      unset: 'Ba a amsa ba',
      skipped: 'An tsallake',
      confirm: 'Daidai ne',
      correct: 'Canza wannan',
    },

    onboarding: {
      disclosureTitle: 'Abin da Namu ke ajiyewa',
      typedLabel: 'Rubuta amsarka',
      typedSubmit: 'Aika',
      progressLabel: 'Yawan abin da Namu ya sani ya zuwa yanzu',
    },

    field: {
      language: 'Yare',
      name: 'Suna',
      region: 'Inda kake zaune',
      ageBand: 'Shekaru',
      gender: 'Jinsi',
      interests: 'Abubuwan sha’awa',
      consent: 'Adana amsoshinka',
    },

    ageBand: {
      under18: 'Ƙasa da 18',
      '18-24': '18 zuwa 24',
      '25-34': '25 zuwa 34',
      '35-49': '35 zuwa 49',
      '50-64': '50 zuwa 64',
      '65plus': '65 ko sama',
    },

    gender: {
      woman: 'Mace',
      man: 'Namiji',
      other: 'Wata amsa',
      undisclosed: 'Ba na so in faɗa',
    },


    error: {
      micDeniedTitle: 'Namu ba ya jinka',
      micDeniedBody:
        'An toshe makurufo don wannan shafin. Ba da izini a saitunan burauzarka, sannan ka sāke gwadawa.',
      micMissingTitle: 'Ba a sami makurufo ba',
      micMissingBody: 'Haɗa makurufo ko belun kunne, sannan ka sāke gwadawa.',
      unsupportedTitle: 'Wannan burauza ba ta iya gudanar da Namu',
      unsupportedBody: 'Namu na buƙatar izinin makurufo. Gwada Chrome, Edge ko Safari.',
      offlineTitle: 'Babu haɗi',
      offlineBody: 'Namu na buƙatar haɗi don amsawa. Zai sāke haɗawa da kansa.',
      connectionFailedTitle: 'Haɗin ya gaza',
      connectionFailedBody: 'Namu bai iya samun muryarsa ba. Danna don sāke gwadawa.',
      genericTitle: 'An sami matsala',
      genericBody: 'Tattaunawar ta tsaya. Ka sāke fara ta don ci gaba.',
      noVoiceTitle: 'Babu murya ga wannan yaren tukuna',
      noVoiceBody:
        'Wannan na’urar ba ta da muryar {language}, don haka Namu zai nuna kalmominsa maimakon ya faɗe su.',
    },

    profile: {
      title: 'Abin da Namu ya sani',
      subtitle: 'Ana adana duk waɗannan a wannan wayar kaɗai.',
      changeByVoice: 'Canza ta hanyar magana',
      deleteAll: 'Goge komai',
      deleteConfirmTitle: 'A goge komai?',
      deleteConfirmBody:
        'Namu zai manta da sunanka, yankinka, shekarunka, jinsinka da abubuwan sha’awarka. Ba za a iya maida wannan ba.',
      deleteConfirmAction: 'Goge',
      cancel: 'Soke',
      savedOn: 'An adana {date}',
    },

    settings: {
      title: 'Saitunan',
      language: 'Yare',
      persona: 'Wanda kake magana da shi',
      captions: 'Koyaushe nuna rubutu',
      haptics: 'Girgiza a lokacin canjin magana',
      reduceMotion: 'Rage motsi',
      appearance: 'Kamanni',
      appearanceSystem: 'Kamar na’ura',
      appearanceLight: 'Haske',
      appearanceDark: 'Duhu',
      diagnostics: 'Bincike',
      terms: 'Sharuɗɗan amfani',
    },

    diagnostics: {
      title: 'Bincike',
      turns: 'Juyi',
      medianLatency: 'Matsakaicin lokacin amsa',
      retries: 'Sau nawa Namu ya sāke tambaya',
      fallbacks: 'Sau nawa aka yi amfani da rubutu',
      interruptions: 'Sau nawa ka katse',
      silenceWindow: 'Lokacin jira na ƙarshen magana',
      reset: 'Share binciken',
      empty: 'Ba a yi rikodin tattaunawa ba tukuna.',
    },

    terms: {
      title: 'Sharuɗɗan amfani',
      updated: 'An sabunta 27 ga Yuli 2026',
      lead: 'Sharuɗɗa a sarari don amfani da Namu. Karanta su sau ɗaya; ba su da tsawo.',
      sections: [
        {
          heading: 'Menene Namu',
          body: 'Namu manhaja ce da ke aiki da murya. Sabuwa ce, kuma wani lokaci za ta yi kuskure, ta ji ba daidai ba, ko ta ba da amsa mara kyau. Ka tabbatar da duk abin da ke da muhimmanci kafin ka yi aiki da shi.',
        },
        {
          heading: 'Wa zai iya amfani da ita',
          body: 'Ya kamata ka kai shekarun da doka a wurinka ta yarda ka amince da sharuɗɗa. Idan ba ka kai ba, ka nemi iyaye ko mai kula da kai ya amince a madadinka kuma ku yi amfani da Namu tare.',
        },
        {
          heading: 'Abin da ake adanawa',
          body: 'Ana adana amsoshinka a wannan wayar kaɗai. Ana amfani da muryarka don fahimtarka yayin magana, kuma ba a ajiye sautin. Namu ba ya yin tambarin murya kuma ba zai iya gane ka ta muryarka ba. Za ka iya duba, canza ko goge komai daga “Abin da Namu ya sani”.',
        },
        {
          heading: 'Abin da Namu ba shi ba ne',
          body: 'Namu ba ya ba da shawarar likita, ta shari’a ko ta kuɗi, kuma ba hidimar gaggawa ba ce. A lokacin gaggawa, ka tuntuɓi hukumomin da ke wurinka.',
        },
        {
          heading: 'Yarukan samfuri',
          body: 'Yaren da aka yi masa alamar “samfuri” ba a duba shi da mai jin yaren ba tukuna. Namu na iya faɗin abu ba daidai ba ko ya kuskure kalma a cikin wannan yaren.',
        },
        {
          heading: 'Amfani da Namu yadda ya dace',
          body: 'Kada ka yi amfani da Namu don cutar da mutane, don karya doka, ko don nuna cewa Namu ya faɗi abin da bai faɗa ba.',
        },
        {
          heading: 'Sunan Namu da alamarsa',
          body: 'Sunan Namu, rubutun sunan da Blossom mallakar Namu ne. Kada ka yi amfani da su don naka kayan ba tare da izini a rubuce ba.',
        },
        {
          heading: 'Canje-canje',
          body: 'Waɗannan sharuɗɗa na iya canzawa yayin da Namu ke girma. Idan hakan ya faru, kwanan wata da ke sama zai canza kuma Namu zai sanar da kai.',
        },
        {
          heading: 'Tuntuɓar mu',
          body: 'Don duk abin da ya shafi waɗannan sharuɗɗa, ka rubuta zuwa legal@namuai.org.',
        },
      ],
    },

    a11y: {
      orb: 'Alamar yanayin Namu',
      orbState: 'Namu yana {state}',
      liveTranscript: 'Rubutun kai tsaye',
      levelMeter: 'Ƙarfin makurufo',
      skipToControls: 'Tsallake zuwa maɓallan',
    },

    common: {
      you: 'Kai',
      namu: 'Namu',
      yes: 'Eh',
      no: 'A’a',
      of: '{current} daga {total}',
      tapToRetry: 'Danna don sāke gwadawa',
      preview: 'Samfuri',
      previewHint: 'Ba a duba wannan yaren da wani mai jinsin magana ba tukuna.',
    },
  },

  speech: {
    micPrimer: [
      'Yanzu ina buƙatar in ji ka. Burauzarka za ta nemi izinin makurufo.',
    ],
    micDenied: [
      'Ban iya jinka ba tukuna. An toshe makurufo. Ka ba da izini a saitunan burauzarka, sannan ka sāke gwadawa.',
    ],
    /**
     * The returning greeting, and the one place a reviewer should expect the
     * Hausa to diverge furthest from the English.
     *
     * Hausa already has a greeting for each of these moments and they are not
     * translations of the English ones. `Ina kwana` asks how you slept and is
     * what the morning greeting actually is. `Na’am` is the word for answering
     * to your own name, which is what "Yes?" is doing. `Kwana biyu`, literally
     * "two days", is the idiom for a long absence and carries the warmth that
     * "it has been a while" carries in English; rendering that one literally
     * would produce a correct sentence nobody says.
     */
    greeting: {
      first: [
        'Sannu. Hello. Ka yi magana da ni da Hausa ko da Turanci, wanda ya fi maka sauƙi.',
        'Sannu. Hello. Ka faɗi abin da kake so, da Hausa ko da Turanci.',
      ],

      named: {
        moments: ['Na’am, {name}?', '{name}?'],
        sameDay: ['Sannu kuma, {name}.', 'Ga mu nan kuma, {name}.'],
        morning: ['Ina kwana, {name}?'],
        afternoon: ['Barka da rana, {name}.'],
        evening: ['Barka da yamma, {name}.'],
        night: ['Barka da dare, {name}.'],
        week: ['Kwana biyu, {name}.'],
        month: ['{name}. Na ji daɗin sāke jin muryarka.'],
      },

      anonymous: {
        moments: ['Na’am?'],
        sameDay: ['Sannu kuma.'],
        morning: ['Ina kwana?'],
        afternoon: ['Barka da rana.'],
        evening: ['Barka da yamma.'],
        night: ['Barka da dare.'],
        week: ['Kwana biyu.'],
        month: ['Na ji daɗin sāke jin muryarka.'],
      },

      continuity: ['Lokacin ƙarshe mun yi magana a kan {topic}.'],
    },
    listeningPrompt: ['Ina saurare.'],
    didNotCatch: ['Yi haƙuri, ban ji ba. Ka sāke faɗi?', 'Ban ji ba. Sau ɗaya kuma?'],
    stillThere: ['Kana nan?'],
    goodbye: ['Sai an jima.', 'Sai wani lokaci.'],
    offline: ['Haɗin ya yanke. Zan sāke gwadawa.'],
    reconnected: ['Na dawo.'],
    errorGeneric: ['An sami matsala a wajena. Ka sāke farawa idan ka shirya.'],
    profileDeleted: ['Na manta da komai. Za mu iya sāke farawa.'],
    acknowledge: ['Na ji.', 'Na gode.', 'Toh.'],
    skipped: ['Za mu tsallake wannan.'],

    modelNotConnected: [
      'Ba a haɗa samfurin harshena ba tukuna. Har yanzu zan iya gaya maka abin da na tuna game da kai, in canza shi, ko in ɗauki sabon suna.',
    ],
    helpOffer: [
      'Za ka iya cewa: me ka sani game da ni. Ko: ka kira ni da wani suna. Ko: sai an jima.',
    ],
    profileReadIntro: ['Ga abin da na tuna.'],
    profileEmpty: ['Ba ni da wani abu da aka adana game da kai tukuna.'],
    nameUpdated: ['Zan kira ka {name} daga yanzu.'],
    languageSwitched: ['Zan yi magana da Hausa yanzu.'],
  },
}
